import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import "../../style.scss";
import { useAppContext } from "../../../../../AppContext";
import { useLocation, useParams } from "react-router-dom";

function EditAccountUser() {
  const [reset, setReset] = useState(new Date().getTime());
  const [title] = useState("Create User | Chimera Solutions | Insights");
  const [form, setForm] = useState({ state: null, message: "" });
  const [data, setData] = useState(null);
  const [field, setField] = useState({
    username: { value: "", message: "", state: null },
    password: { value: "", state: null, message: "" },
    rePassword: { value: "", message: "", state: null },
    email: { value: "", message: "", state: null },
  });
  const [load, setLoad] = useState(false);
  const { user, chimeraserv, components } = useAppContext();
  const location = useLocation();
  const { uid } = useParams();
  const props = {
    user,
    chimeraserv,
    location,
    match: { params: { uid } },
    components,
  };

  const timeRef = useRef(null);

  const headers = {
    Authorization: props.user.data.usrKey,
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  // Title rendering
  const renderTitle = () => (
    <Helmet>
      <title>{title}</title>
    </Helmet>
  );

  // Validate fields
  const validateFields = () => {
    setForm((prev) => ({ ...prev, state: false }));
    if (field.username.state && field.rePassword.state && field.email.state) {
      setForm((prev) => ({ ...prev, state: true }));
    }
  };

  // Check field
  const checkField = (element) => {
    clearTimeout(timeRef.current);
    timeRef.current = setTimeout(async () => {
      let id = element.id;
      let value = element.value;
      switch (id) {
        case "username": {
          let state = true;
          let message = "";
          if (value.length <= 3) {
            state = false;
            message = "username should be minimum of 4 characters";
          }
          try {
            const response = await axios.get(
              props.chimeraserv.urlGet + "/user",
              { headers }
            );
            if (
              response.data.find((result) => result.username === value) &&
              data?.username !== value
            ) {
              state = false;
              message = "username already exist";
            }
          } catch {}
          setField((prev) => ({
            ...prev,
            [id]: { ...prev[id], state, message },
          }));
          break;
        }
        case "password": {
          let state = false;
          let message = "";
          if (value.length <= 7) {
            message = "password should be minimum of 8 characters";
          } else {
            state = true;
            message = "";
          }
          setField((prev) => ({
            ...prev,
            [id]: { ...prev[id], state, message },
          }));
          break;
        }
        case "rePassword": {
          let state = false;
          let message = "";
          if (field.password.value !== value) {
            message = "passwords did not match";
          } else {
            state = true;
            message = "";
          }
          setField((prev) => ({
            ...prev,
            [id]: { ...prev[id], state, message },
          }));
          break;
        }
        case "email": {
          let state = true;
          let message = "";
          // eslint-disable-next-line
          const regex =
            /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          if (!regex.test(String(value).toLowerCase())) {
            state = false;
            message = "invalid email address";
          }
          try {
            const response = await axios.get(
              props.chimeraserv.urlGet + "/user",
              { headers }
            );
            if (
              response.data.find((result) => result.email === value) &&
              data?.email !== value
            ) {
              state = false;
              message = "email address already exist";
            }
          } catch {}
          setField((prev) => ({
            ...prev,
            [id]: { ...prev[id], state, message },
          }));
          break;
        }
        default:
          break;
      }
      validateFields();
    }, 500);
  };

  // Value changed handler
  const valueChanged = (event) => {
    const element = event.target;
    setField((prev) => ({
      ...prev,
      [element.id]: {
        ...prev[element.id],
        value: element.value,
        message: "",
        state: null,
      },
    }));
    setForm((prev) => ({ ...prev, state: false }));
    checkField(element);
  };

  // Reset
  const resetForm = () => setReset(new Date().getTime());

  // Load data
  const loadData = async () => {
    try {
      const response = await axios.get(
        props.chimeraserv.urlGet + "/user/" + props.match.params.uid,
        { headers }
      );
      if (response.data && response.data.length) {
        const userData = response.data[0];
        setData(userData);
        setLoad(true);
        setField((prev) => ({
          ...prev,
          username: { ...prev.username, value: userData.username, state: true },
          email: { ...prev.email, value: userData.email, state: true },
          password: { ...prev.password, value: "", state: null, message: "" },
          rePassword: {
            ...prev.rePassword,
            value: "",
            state: null,
            message: "",
          },
        }));
      }
    } catch {}
  };

  // Effect for loading data on mount and reset
  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, [reset]);

  // Checker
  const checker = (state) => {
    const check = { icon: "check-circle", style: "null" };
    switch (state) {
      case true:
        check.icon = "check-circle";
        check.style = "correct";
        break;
      case false:
        check.icon = "times-circle";
        check.style = "wrong";
        break;
      default:
        break;
    }
    return check;
  };

  // Submit
  const submit = async () => {
    setForm((prev) => ({ ...prev, state: false, message: "" }));
    try {
      const response = await axios.put(
        props.chimeraserv.urlPost + "/user/update",
        {
          ...data,
          username: field.username.value,
          password: field.password.value,
          email: field.email.value,
        },
        {
          headers: {
            Authorization: props.user.data.usrKey,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );
      if (response.data && !response.data.__v) {
        resetForm();
        setForm((prev) => ({ ...prev, message: "Account Updated" }));
      }
    } catch (error) {
      setForm((prev) => ({
        ...prev,
        message: JSON.stringify(error),
      }));
    }
  };

  // Generate password
  const genPass = () => {
    const length = 8;
    const charset = `!@#$%^&*()-=abcdefghijklmnopqrstuvwxyz0123456789`;
    const retVal = [];
    for (let i = 0, n = charset.length; i < length; ++i) {
      let char = charset.charAt(Math.floor(Math.random() * n));
      if (Math.round(Math.random())) {
        retVal.push(char.toUpperCase());
      } else {
        retVal.push(char.toLowerCase());
      }
    }
    const password = String(retVal.join(""));
    setField((prev) => ({
      ...prev,
      password: {
        ...prev.password,
        message: `Generated new password: ${password}`,
        value: password,
        state: true,
      },
      rePassword: {
        ...prev.rePassword,
        value: password,
        state: true,
      },
    }));
    setTimeout(() => validateFields(), 0);
  };

  // Loader component
  const LoaderComponent = props.components.setting[1].component;

  return (
    <div className={"custForm"}>
      {renderTitle()}
      <div className={["viewTitle"]}>Edit My Account</div>
      {!load ? (
        <LoaderComponent />
      ) : (
        <form className={"form"}>
          <div className={["form-group"]}>
            <label>
              Username <span>*</span>
            </label>
            <FontAwesomeIcon
              className={["icon", checker(field.username.state).style].join(
                " "
              )}
              icon={["fas", checker(field.username.state).icon]}
            />
            <div className={["field"]}>
              <input
                type={"text"}
                className={["form-control"]}
                id={"username"}
                placeholder={"username"}
                value={field.username.value}
                onChange={valueChanged}
              />
              <span>{field.username.message}</span>
            </div>
          </div>
          <div className={["form-group"]}>
            <label>
              Password <span>*</span>
            </label>
            <FontAwesomeIcon
              className={["icon", checker(field.password.state).style].join(
                " "
              )}
              icon={["fas", checker(field.password.state).icon]}
            />
            <div className={["field"]}>
              <div className={["password"].join(" ")}>
                <input
                  type={"password"}
                  className={["form-control"]}
                  id={"password"}
                  placeholder={"password"}
                  value={field.password.value}
                  onChange={valueChanged}
                />
                <button type={"button"} onClick={genPass}>
                  <FontAwesomeIcon
                    className={["icon"].join(" ")}
                    icon={"sync-alt"}
                  />
                </button>
              </div>
              <span>{field.password.message}</span>
            </div>
          </div>
          <div className={["form-group"]}>
            <label>
              Re-type Password <span>*</span>
            </label>
            <FontAwesomeIcon
              className={["icon", checker(field.rePassword.state).style].join(
                " "
              )}
              icon={["fas", checker(field.rePassword.state).icon]}
            />
            <div className={["field"]}>
              <input
                type={"password"}
                className={["form-control"]}
                id={"rePassword"}
                placeholder={"re-type password"}
                value={field.rePassword.value}
                onChange={valueChanged}
              />
              <span>{field.rePassword.message}</span>
            </div>
          </div>
          <div className={["form-group"]}>
            <label>
              Email Address <span>*</span>
            </label>
            <FontAwesomeIcon
              className={["icon", checker(field.email.state).style].join(" ")}
              icon={["fas", checker(field.email.state).icon]}
            />
            <div className={["field"]}>
              <input
                type={"text"}
                className={["form-control"]}
                id={"email"}
                placeholder={"email address"}
                value={field.email.value}
                onChange={valueChanged}
              />
              <span>{field.email.message}</span>
            </div>
          </div>
          {form.message ? (
            <div className={["message", "form-group"].join(" ")}>
              <FontAwesomeIcon
                className={["icon", "correct"].join(" ")}
                icon={"check-circle"}
              />
              {form.message}
            </div>
          ) : (
            <div />
          )}
          <button
            type={"button"}
            className={["btn btn-primary"]}
            disabled={!form.state}
            onClick={submit}
          >
            Update
          </button>
        </form>
      )}
    </div>
  );
}
export default EditAccountUser;
